import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

const QUESTION_MAX_LENGTH = 300;

export const SimilarMindsHostPrompt: React.FC = () => {
  const { socket, gameState, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const sm = t.games.similarMinds;
  const [question, setQuestion] = React.useState('');

  if (!gameState || !currentRoom) {
    return null;
  }

  const hostId = gameState.similarMindsHostId;
  const hostPlayer = currentRoom.players.find((p) => p.id === hostId);
  const isQuestionHost = myPlayerId === hostId;

  const handleSubmitQuestion = () => {
    const trimmed = question.trim();
    if (!socket || !isQuestionHost || !trimmed) return;
    socket.emit('client:similar-minds-start-answers', trimmed);
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-5">
        <p className="text-sm font-semibold text-teal-700 mb-1">
          {sm.questionHostLabel}: <span className="text-teal-900">{hostPlayer?.name ?? '—'}</span>
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
          {sm.hostPromptTitle}
        </h2>
      </div>

      {isQuestionHost ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
            <p className="text-sm sm:text-base text-gray-700 mb-3">{sm.hostPromptInstruction}</p>
            <p className="text-base sm:text-lg font-semibold text-teal-900 italic">&ldquo;{sm.hostPromptExample}&rdquo;</p>
            <p className="text-sm font-bold text-teal-700 mt-3">{sm.youAreQuestionHost}</p>
          </div>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, QUESTION_MAX_LENGTH))}
            rows={4}
            maxLength={QUESTION_MAX_LENGTH}
            placeholder={sm.questionPlaceholder}
            className="w-full p-3 border-2 border-teal-300 rounded-xl focus:outline-none focus:border-teal-500"
          />
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
            <span>
              {question.length}/{QUESTION_MAX_LENGTH}
            </span>
          </div>
          <button
            onClick={handleSubmitQuestion}
            disabled={question.trim().length === 0}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
          >
            {sm.hostPromptStartButton}
          </button>
        </div>
      ) : (
        <div className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-center">
          <p className="text-base sm:text-lg text-gray-700">{sm.waitingForHostPrompt}</p>
        </div>
      )}
    </div>
  );
};
